/**
 * RevisitScreen — replaces the LeadForm step machine when the user has
 * already submitted a lead for the same `(leadSource × configurationId)`
 * within the dedupe window (7 days, see `lib/leadFormStorage`).
 *
 * Renders the SAME container chrome as the form would, so the slot the
 * caller carved out for `<LeadForm>` stays the same height-ish and the
 * page doesn't reflow when we swap to this view. Three actions:
 *
 *   1. WhatsApp the specialist (primary, green pill — same affordance
 *      the form's Step 2 has, so users recognize it as "the way").
 *   2. "Send another request" — explicit escape hatch when the user
 *      wants to actually submit again. Clears the dedupe entry and
 *      kicks the parent back to the form via `onSendAnother`.
 *   3. Close — only rendered when the parent is a modal (passes
 *      `onClose`); on inline-in-hero mounts there's nothing to close.
 *
 * No mutation here. Pixel firing is the caller's job — pass
 * `onWhatsappClick` if you want to attribute the click.
 */

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'

import type { LeadFormContainer } from 'src/components/LeadForm'
import { buildWhatsappUrl } from 'src/utils/whatsapp'

import WhatsappIcon from '../../../public/svgs/whatsapp-icon.svg'

interface RevisitScreenProps {
  /**
   * Same container affordance the LeadForm uses. Drives the wrapper
   * spacing so the swap is visually consistent.
   */
  container: LeadFormContainer
  /**
   * Optional close handler — only present in modal mounts. Inline-hero
   * surfaces (Flotillas) pass nothing.
   */
  onClose?: () => void
  /**
   * "Send another request" callback. Parent should clear the dedupe
   * entry for this lead key AND flip its `revisit` state back to false
   * so the step machine re-renders.
   */
  onSendAnother: () => void
  /**
   * Optional pre-built WhatsApp URL — when the caller has listing
   * context worth threading into the prefill (location name, link),
   * it passes a richer URL here. When absent, we fall back to the
   * generic localized "follow-up" message from `LEAD_FORM.REVISIT.
   * WHATSAPP_MESSAGE`. Either way, sales recognizes the message
   * carries no operational detail — that's the point.
   */
  whatsappUrl?: string
  /**
   * Optional click hook for analytics/pixel firing on the WA action.
   */
  onWhatsappClick?: () => void
}

export default function RevisitScreen({
  container,
  onClose,
  onSendAnother,
  whatsappUrl,
  onWhatsappClick
}: RevisitScreenProps) {
  const { t } = useTranslation('details')
  const fallbackUrl = buildWhatsappUrl(t('LEAD_FORM.REVISIT.WHATSAPP_MESSAGE'))
  const resolvedUrl = whatsappUrl ?? fallbackUrl

  // Match the LeadForm's containerClassName — keeps spacing rhythm
  // identical between the two views (including the tighter rhythm on
  // inline-on-card, where the price-card right rail is height-constrained).
  const containerClasses =
    container === 'page-section'
      ? 'flex flex-col gap-6'
      : container === 'inline-on-card'
        ? 'flex flex-col gap-3'
        : 'flex flex-col gap-5'

  return (
    <section className={containerClasses} data-revisit-screen>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <CheckCircleIcon className="text-[20px] text-green-500" />
          <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
            {t('LEAD_FORM.REVISIT.EYEBROW')}
          </p>
        </div>
        <h3 className="font-display text-[22px] leading-tight font-medium text-ink-700 md:text-[24px]">
          {t('LEAD_FORM.REVISIT.TITLE')}
        </h3>
        <p className="text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
          {t('LEAD_FORM.REVISIT.SUBTITLE')}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <a
          href={resolvedUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onWhatsappClick}
          className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-green-500 px-7 py-3.5 text-center text-[15px] font-semibold text-white-500 shadow-[0_20px_40px_-16px_rgba(34,197,94,0.55)] transition-all duration-200 hover:bg-green-600 active:scale-[0.98]"
        >
          <Image
            src={WhatsappIcon}
            alt=""
            width={16}
            height={16}
            aria-hidden="true"
          />
          {t('LEAD_FORM.REVISIT.WHATSAPP_CTA')}
        </a>

        <button
          type="button"
          onClick={onSendAnother}
          className="text-[13px] font-medium text-blue-500 underline-offset-4 hover:underline"
        >
          {t('LEAD_FORM.REVISIT.SEND_ANOTHER')}
        </button>

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="text-[12px] text-ink-200 underline-offset-4 hover:underline"
          >
            {t('LEAD_FORM.REVISIT.CLOSE')}
          </button>
        )}
      </div>
    </section>
  )
}
